import { Component, Input, ViewChild, EventEmitter, Output, DoCheck, AfterContentInit, inject, ChangeDetectionStrategy } from '@angular/core';

import { HisBaseGlobalsService } from '../../../core/service/base/his-base-globals.service';

import { DynamicComponent, DynamicObjects, GUIComponent } from '../../model/dynamic-content';
import { HisDynamicContentUtils } from '../../model/dynamic-content-utils';
import { HisDynamicFieldConfig, HisDynamicFieldType } from '../../model/formfield';
import { HisDynamicContainerComponent } from '../input-controls/dynamic-container.component';

@Component( {
    selector: 'his-dynamic-form-container',
    templateUrl: './dynamic-form-container.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class DynamicFormContainerComponent implements DoCheck, AfterContentInit {
    hisBaseGlobalsService = inject(HisBaseGlobalsService);
    private hisDynamicContentUtils = inject(HisDynamicContentUtils);

    @Input() dynamicComponent: DynamicComponent;
    @Input() dynamicComponentTabName: string;
    @ViewChild( HisDynamicContainerComponent, { static: false } ) dynamicContainerComponent: HisDynamicContainerComponent;
    @Output() outputEvent: EventEmitter<Record<string, any>> = new EventEmitter<Record<string, any>>();

    public componentId: string;
    public guiComponent: GUIComponent;
    public dynamicFieldConfig: HisDynamicFieldConfig;

    private formGroupValue: Record<string, any> = {};
    private dynamicObjects: DynamicObjects[];

    ngAfterContentInit() {
        this.setupDynamicContainerComponent();
    }

    ngDoCheck() {
        if ( this.dynamicObjects && this.componentId ) {
            let newFormGroupValue: Record<string, any> = this.dynamicContainerComponent ?
                this.dynamicContainerComponent.getJson() : {};
            // if dynamicFieldConfig is type of field
            if ( this.componentId.includes('field_')
                && ( newFormGroupValue && this.dynamicFieldConfig.children[0]
                && !newFormGroupValue[this.dynamicFieldConfig.children[0].name] ) ) {
                newFormGroupValue[this.dynamicFieldConfig.children[0].name] = null;
            }
            // check formGroupValue object for changes
            if ( JSON.stringify( this.formGroupValue ) !== JSON.stringify( newFormGroupValue ) ) {
                this.outputEvent.emit( newFormGroupValue );
            }
        }
    }

    public setupDynamicContainerComponent() {
        if ( this.dynamicFieldConfig == null && this.dynamicComponent ) {
            this.createDynamicFieldConfig( this.dynamicComponent );
        }
        this.formGroupValue = this.dynamicContainerComponent ?
            this.dynamicContainerComponent.getJson() : {};
    }

    public createDynamicFieldConfig( dynamicComponent: DynamicComponent,
        fieldComponent?: GUIComponent, dynamicObjects?: DynamicObjects[] ): void {
        this.dynamicObjects = dynamicObjects ? dynamicObjects : dynamicComponent.dynamicObjects;
        this.guiComponent = fieldComponent;

        this.dynamicFieldConfig = {
            name: this.componentId + 'DynamicForm',
            controlType: HisDynamicFieldType.Form,
            label: this.componentId + 'DynamicForm',
            children: [],
        };

        let dynamicTabChild = null;
        if ( dynamicObjects ) {
            this.addFieldToConfig( fieldComponent );
        } else {
            dynamicTabChild = dynamicComponent.guiComponent.children.find(
                tabChild => tabChild.name === this.dynamicComponentTabName );
            if ( dynamicTabChild ) {
                dynamicTabChild.children.forEach( ( guiComponent: GUIComponent ) => {
                    this.addFieldToConfig( guiComponent );
                } );
            }
        }
    }

    private addFieldToConfig( guiComponent: GUIComponent ) {
        let fieldToAdd: HisDynamicFieldConfig = null;
        if ( this.hisDynamicContentUtils.checkForYesNoWithTextArea( guiComponent, this.dynamicObjects ) ) {
            fieldToAdd = this.generateFieldConfig( guiComponent );

            let orderedChildren = [];
            orderedChildren[0] = fieldToAdd.children.find( child => {
                return child.controlType === HisDynamicFieldType.YesNoSelect;
            } );
            orderedChildren[0].showLabelTop = true;
            
            orderedChildren[1] = fieldToAdd.children.find( child => {
                // set field type to TextArea
                if ( child.controlType === HisDynamicFieldType.Text ) {
                    child.controlType = HisDynamicFieldType.TextArea;
                    return child;
                } else {
                    return child.controlType === HisDynamicFieldType.TextArea;
                }
            } );
            orderedChildren[1].showLabelTop = true;

            fieldToAdd.children = orderedChildren;
        } else if ( this.isNotTypedFieldsetAndNotEmpty( guiComponent ) ) {
            fieldToAdd = this.generateFieldConfig( guiComponent );
        }


        if ( fieldToAdd != null ) {
            this.dynamicFieldConfig.children.push( fieldToAdd );
        }
    }

    private generateFieldConfig( field: GUIComponent ): HisDynamicFieldConfig {
        let dynamicField: HisDynamicFieldConfig = {
            name: field.name,
            controlType: ( HisDynamicFieldType as any )[field.controlType],
            label: field.controlType === HisDynamicFieldType.Fieldset || field.controlType === HisDynamicFieldType.YesNoWithTextArea
                ? field.name : null,
            required: this.hisBaseGlobalsService.getDd( field.ddKey, this.dynamicComponent.conditions.contexts ) ? this.hisBaseGlobalsService.getDd( field.ddKey, this.dynamicComponent.conditions.contexts ).required : false,
            ddKey: field.controlType === HisDynamicFieldType.Fieldset || field.controlType === HisDynamicFieldType.YesNoWithTextArea
                ? field.name : field.ddKey,
            typeAheadUrl: field.controlType === HisDynamicFieldType.KeytableSelect
                || field.controlType === HisDynamicFieldType.KeytableMultiSelect
                ? this.hisDynamicContentUtils.getDynamicObjectAttribute(
                    this.dynamicObjects, field.objectUID, field.attributeName, 'source' ) : null,
            children: field.fields && field.fields.length !== 0 ? this.getFieldsArray( field.fields ) : [],
            defaultValue: this.hisDynamicContentUtils.getDynamicObjectAttribute(
                this.dynamicObjects, field.objectUID, field.attributeName, 'value' ),
            oldValue: this.hisDynamicContentUtils.getDynamicObjectAttribute(
                this.dynamicObjects, field.objectUID, field.attributeName, 'oldValue' ),
            useOldValue: this.hisDynamicContentUtils.getDynamicObjectAttribute(
                this.dynamicObjects, field.objectUID, field.attributeName, 'useOldValue' ) as any,
            disabled: field.rightLevel !== 1 ? true : false,
        };
        return dynamicField;
    }

    private getFieldsArray( fieldsetFields: GUIComponent[] ): HisDynamicFieldConfig[] {
        let fields: HisDynamicFieldConfig[] = [];
        fieldsetFields.forEach( ( field: GUIComponent ) => {
            let childFieldToAdd = this.generateFieldConfig( field );
            if ( childFieldToAdd != null ) {
                fields.push( this.generateFieldConfig( field ) );
            }
        } );
        return fields;
    }

    public isNotTypedFieldsetAndNotEmpty( guiComponent: GUIComponent ) {
        return ( ( guiComponent.controlType === HisDynamicFieldType.DeclarationFieldset || guiComponent.controlType === HisDynamicFieldType.YesNoWithTextArea )
            && guiComponent.fields.length === 2 )
            || ( guiComponent.controlType !== HisDynamicFieldType.DeclarationFieldset && guiComponent.controlType !== HisDynamicFieldType.YesNoWithTextArea );
    }   

    public updateFieldComponent( dynamicFieldsComponent: GUIComponent, dynamicObjects: DynamicObjects[] ) {
        if ( this.dynamicContainerComponent ) {
            this.dynamicFieldConfig.children[0].defaultValue = this.hisDynamicContentUtils.getDynamicObjectAttribute(
                dynamicObjects, dynamicFieldsComponent.objectUID, dynamicFieldsComponent.attributeName, 'value' );
            this.dynamicContainerComponent.updateConfiguration( this.dynamicFieldConfig );
        }
    }

}
